import ChevronDown from '@mdi/svg/svg/chevron-down.svg?react';
import ChevronUp from '@mdi/svg/svg/chevron-up.svg?react';
import Clock from '@mdi/svg/svg/clock-outline.svg?react';
import {
  Button,
  Flex,
  Grid,
  IconText,
  type KeyAction,
  KeyActions,
  MDIIcon,
} from '@solinkcorp/solink-components';
import { getLocalTimeZone } from '@solinkcorp/utils';
import classNames from 'classnames';
import { type IEvent } from 'clients/PlatformApi';
import EventDetails from 'components/EventsPage/EventDetails';
import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import TimeFormatter from 'utils/TimeFormatter';
import { type VideoDiscoverySite } from 'utils/types';

import 'components/VideoPlayer/components/PlayerOverlay/PlayerOverlay.sass';
import { getColorForEvent } from 'styles/eventColors';
import styles from './EventDetailsSnapshotOverlay.module.sass';

interface EventDetailsSnapshotOverlayProps {
  event: IEvent;
  location: VideoDiscoverySite;
  canExpand?: boolean;
}

const EventDetailsSnapshotOverlay: React.FC<EventDetailsSnapshotOverlayProps> = (props) => {
  const { event, location, canExpand } = props;
  const locale = useLocale();

  const receiptContainerRef = useRef<HTMLDivElement>(null);
  const [receiptContainerTransform, setReceiptContainerTransform] = useState(0);

  const { details, startTime, title } = event;

  const hasReceipt = !!details?.['Receipt'];
  const receiptCollapsed = !!receiptContainerTransform || !hasReceipt;
  const showCollapseIcon = canExpand && hasReceipt;
  const timezone = location?.timezone ?? getLocalTimeZone();

  const toggleReceipt = useCallback(
    (
      event?:
        | React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
        | KeyboardEvent
        | undefined,
    ) => {
      if (event) {
        event.stopPropagation();
        const element = event.target as HTMLElement;
        if (element?.tagName === 'PRE' || !canExpand) {
          return;
        }
      }

      const receiptContainer = receiptContainerRef.current;
      if (!receiptContainer) {
        return;
      }

      setReceiptContainerTransform(
        receiptContainerTransform === 0 ? 0 - receiptContainer.offsetHeight : 0,
      );
    },
    [receiptContainerTransform, canExpand],
  );

  const shortcuts = useMemo<Array<KeyAction>>(
    () => [
      {
        shortcut: 't',
        action: toggleReceipt,
        shouldActionRun: () => !!canExpand,
        info: {
          title: locale.videoPlayerOptions.collapseExpandReceipts,
          group: locale.videoPlayerOptions.playerControls,
        },
      },
    ],
    [locale, canExpand, toggleReceipt],
  );

  const eventColor = getColorForEvent(event, false);

  const receiptContainerClass = classNames(
    'receipt-container',
    'receipt-transform',
    styles.receipt,
  );

  const receiptTopContainerClass = classNames('receipt-top-container', {
    [styles.headerCollapsed]: receiptCollapsed,
  });

  const gridTemplateRows = `31px ${receiptCollapsed ? 0 : 'auto'}`;

  return (
    <KeyActions actions={shortcuts}>
      <Grid
        id={styles.overlay}
        className="video-receipt-wrapper"
        gridTemplateRows={gridTemplateRows}
        width="auto"
      >
        <Grid.Item
          className={receiptTopContainerClass}
          gridRow="1 / 2"
          width="100%"
          justifySelf="flex-start"
          style={{
            ...(showCollapseIcon ? null : { padding: '0 18px' }),
            cursor: showCollapseIcon ? 'pointer' : 'default',
            background: eventColor,
          }}
        >
          <Flex
            alignItems="center"
            className="receipt-header-inner"
            height="100%"
            justifyContent="space-between"
            width="auto"
          >
            <Flex alignItems="center" className="receipt-buttons-wrapper">
              {showCollapseIcon && (
                <Button
                  colorMode="light"
                  className="receipt-collapse-toggle"
                  iconOnly
                  icon={
                    <MDIIcon
                      icon={receiptCollapsed ? ChevronDown : ChevronUp}
                      fill="var(--color-white)"
                    />
                  }
                  onClick={toggleReceipt}
                  type="clear"
                  tooltip={receiptCollapsed ? locale.general.expand : locale.general.collapse}
                />
              )}
            </Flex>
            <Flex alignItems="center" className="receipt-title-wrapper" onClick={toggleReceipt}>
              <div className={styles.title}>{title}</div>
              <IconText
                icon={<MDIIcon icon={Clock} size={20} fill="var(--color-white)" />}
                text={moment(startTime).tz(timezone).format(TimeFormatter.getString())}
                style={{ marginLeft: '10px' }}
              />
            </Flex>
          </Flex>
        </Grid.Item>
        <Grid.Item
          gridRow="2 / 3"
          style={receiptCollapsed ? { pointerEvents: 'none' } : {}}
          width="100%"
        >
          {showCollapseIcon && (
            <div
              ref={receiptContainerRef}
              className={receiptContainerClass}
              style={{
                transform: `translate(0, ${!canExpand ? 0 : receiptContainerTransform}px)`,
              }}
            >
              <EventDetails timezone={timezone} event={event} />
            </div>
          )}
        </Grid.Item>
      </Grid>
    </KeyActions>
  );
};

export default EventDetailsSnapshotOverlay;
